import React from 'react'

import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LockLockedFillIcon } from '@arcade/web/icons/react'
import { FieldLabel } from '@cash-design-system/react'
import { isFirstDigitsAmex, isValidCardNumber } from '@cash-web/shared-util-validation'

import { FormMaskedInput } from './FormMaskedInput'

export type CardNumberType = 'DEBIT_CARD' | 'CREDIT_CARD'
export type CardNumberProps = {
  name: string
  type: CardNumberType
  autoFocus?: boolean
}
const CardNumberWrapper = styled.div`
  position: relative;
`

export const CardNumber: React.FC<CardNumberProps> = ({ name, autoFocus, type, ...rest }) => {
  const { t } = useTranslation()
  const { watch } = useFormContext()
  const watchCardNumber = watch(name)
  const isAmex = isFirstDigitsAmex(watchCardNumber)

  const { label, placeholder, requiredText, invalidText } = (() => {
    switch (type) {
      case 'DEBIT_CARD':
        return {
          label: t('Debit card number', { context: 'label for debit card number form field' }),
          placeholder: t('0000 0000 0000 0000', { context: 'placeholder for debit card number form field' }),
          requiredText: t('Debit card number is required.', {
            context: 'error message for debit card number form field',
          }),
          invalidText: t('Invalid debit card number.', { context: 'error message for debit card number form field' }),
        }
      case 'CREDIT_CARD':
        return {
          label: t('Credit card number', { context: 'label for credit card number form field' }),
          placeholder: t('0000 0000 0000 0000', { context: 'placeholder for credit card number form field' }),
          requiredText: t('Enter a credit card number.', {
            context: 'error message for credit card number form field',
          }),
          invalidText: t('Invalid credit card number.', { context: 'error message for credit card number form field' }),
        }
    }
  })()

  return (
    <CardNumberWrapper>
      <FieldLabel label={label}>
        <FormMaskedInput
          name={name}
          placeholder={placeholder}
          autoComplete="cc-number"
          autoFocus={autoFocus}
          format={isAmex ? '#### ###### #####' : '#### #### #### ####'}
          rules={{
            required: {
              value: true,
              message: requiredText,
            },
            validate: value => isValidCardNumber(value) || invalidText,
          }}
          endAdornment={<LockLockedFillIcon size={24} data-testid="lock" />}
          {...rest}
        />
      </FieldLabel>
    </CardNumberWrapper>
  )
}
